import React from 'react';
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import type { LookupSelectionMode } from './lookupTypes';

interface LookupFieldProps {
  id: string;
  label: string;
  required?: boolean;
  mode: LookupSelectionMode;
  /** Single-select display value */
  displayValue?: string;
  /** Multi-select chip labels */
  chipItems?: Array<{ id: string; label: string }>;
  onOpenLookup: () => void;
  onRemoveChip?: (id: string) => void;
  onClear?: () => void;
  error?: string;
  disabled?: boolean;
  placeholder?: string;
  hint?: string;
}

const LookupField: React.FC<LookupFieldProps> = ({
  id,
  label,
  required = false,
  mode,
  displayValue,
  chipItems = [],
  onOpenLookup,
  onRemoveChip,
  onClear,
  error,
  disabled = false,
  placeholder = '—',
  hint,
}) => {
  const hasValue = mode === 'single' ? Boolean(displayValue) : chipItems.length > 0;

  return (
    <div className="grid grid-cols-12 gap-3 items-start">
      <div className="col-span-12 sm:col-span-4 lg:col-span-3 pt-2 text-right sm:pr-2">
        <label htmlFor={id} className="text-sm font-medium text-gray-700">
          {required && <span className="text-red-500 mr-0.5" aria-hidden>*</span>}
          {label}
        </label>
      </div>
      <div className="col-span-12 sm:col-span-8 lg:col-span-9">
        <div className="flex items-start gap-1">
          <div
            className={`flex-1 min-h-[32px] flex flex-wrap items-center gap-1.5 px-2 py-1 border rounded text-sm ${
              error ? 'border-red-500 ring-1 ring-red-500' : 'border-gray-300'
            } ${disabled ? 'bg-gray-100' : 'bg-white'}`}
          >
            {mode === 'single' ? (
              <span className={displayValue ? 'text-gray-900' : 'text-gray-400'}>
                {displayValue || placeholder}
              </span>
            ) : chipItems.length > 0 ? (
              chipItems.map((chip) => (
                <span
                  key={chip.id}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-indigo-50 text-indigo-800 border border-indigo-200 text-xs"
                >
                  {chip.label}
                  {!disabled && onRemoveChip && (
                    <button
                      type="button"
                      onClick={() => onRemoveChip(chip.id)}
                      className="text-indigo-500 hover:text-indigo-800"
                      aria-label={`Remove ${chip.label}`}
                    >
                      <XMarkIcon className="h-3.5 w-3.5" />
                    </button>
                  )}
                </span>
              ))
            ) : (
              <span className="text-gray-400">{placeholder}</span>
            )}
          </div>
          <button
            type="button"
            id={id}
            onClick={onOpenLookup}
            disabled={disabled}
            className="inline-flex items-center justify-center h-8 w-8 border border-gray-300 rounded bg-gray-50 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label={`Search ${label}`}
            title={`Search ${label}`}
          >
            <MagnifyingGlassIcon className="h-4 w-4 text-gray-600" />
          </button>
          {hasValue && onClear && !disabled && (
            <button
              type="button"
              onClick={onClear}
              className="inline-flex items-center justify-center h-8 w-8 border border-gray-300 rounded bg-white hover:bg-gray-50"
              aria-label={`Clear ${label}`}
              title={`Clear ${label}`}
            >
              <XMarkIcon className="h-4 w-4 text-gray-500" />
            </button>
          )}
        </div>
        {hint && <p className="mt-1 text-xs text-gray-500">{hint}</p>}
        {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
      </div>
    </div>
  );
};

export default LookupField;
